/*
 * Copyright (c) 2020 Apricad LLC. All Rights Reserved.
 */

import * as React from "react";
import { useState, useEffect, useRef } from "react";
import { connect } from "react-redux";
import { useTheme } from "@material-ui/core/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import classNames from "classnames";
import * as moment from "moment";

import {
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  IconButton,
  FormControlLabel,
  Checkbox,
} from "@material-ui/core";
import { Close, ThumbUp, MoodBad } from "@material-ui/icons";

import { ExSel } from "client/views/Exercise/ExSel";
import { ExFill } from "client/views/Exercise/ExFill";
import { ExSortable } from "client/views/Exercise/ExSortable";
import { Modal } from "components/Modal/Modal";
import { progressCircles } from "components/Form/FormField";
import { isCtMan } from "client/reducers/userReducer";

import {
  sBack,
  sQuitTest,
  sQuitTestMsg,
  sTestConfirm,
  sSkipped,
  sAnsCorrect,
  sExFeedback,
  sExThink,
} from "assets/jss/views/ResStrings";

import { getOwnTest, setOwnTest, ApricotAction } from "client/actions/own";
import { TestData } from "client/reducers/ownReducer";

import createStyles from "@material-ui/core/styles/createStyles";
import withStyles, { WithStyles } from "@material-ui/core/styles/withStyles";

import exerciseStyle from "assets/jss/views/exercise/exerciseStyle.jsx";
const styles = createStyles(exerciseStyle);

export type TestResult = {
  rand?: number[];
  userAnsBool?: boolean[];
  userAnsNum?: number[];
  userAnsStr?: string;
  correct?: boolean;
};

export type TestActionParams = {
  action: "next" | "quit";
  data: TestResult;
};

interface Props extends WithStyles<typeof styles> {
  getOwnTest: ApricotAction;
  setOwnTest: ApricotAction;
  // store data
  user: any;
  bid: string;
  testData: TestData;
  currentTestId: string;
  // component props
  classes: any;
  cid: string;
  isTest: boolean;
  exercises: any;
  timeLimit: number;
  points: number[];
  fbk: number;
  onAction: (actoin: Object) => void;
}

const ExMainBase: React.FC<Props> = ({
  getOwnTest,
  setOwnTest,
  user,
  cid,
  bid,
  testData,
  currentTestId,
  classes,
  isTest,
  exercises,
  timeLimit,
  points,
  fbk,
  onAction,
}: Props) => {
  const [s_testNum, setTestNum] = useState<number>(-1);
  const [s_timer, setTimer] = useState<boolean>(false);
  const [s_elapse, setElapse] = useState<number>(0);
  const [s_dataReady, setDataReady] = useState(false);
  const [s_quit, setQuit] = useState(false);

  const [showAlert, setShowAlert] = useState(false);
  const [alertModal, setAlertModal] = useState<React.ReactElement | null>(null);
  const [confirm1, setConfirm1] = useState(false);
  const [confirm2, setConfirm2] = useState(false);

  let elapse = 0;

  const testResults = useRef<any[]>([]);

  // points: Use testPts to accumulate the point of each test item in "next" action.
  // When points of all items are collected, set total point in s_points.
  // This also means we calculate the points during the test, not after the test. The points is
  // written to DB, so when revisiting the test, we don't need to calculate the points.
  const [s_points, setPoints] = useState<number>(-1);
  const testPts = useRef<number[]>([]);

  const ExComponent: React.ReactNode = [
    ExSel,
    ExSel,
    ExFill,
    ExSortable,
    ExSortable,
    ExSortable,
  ];

  useEffect(() => {
    getOwnTest(user._id, bid, !testData || !testData[bid]);
  }, []);

  useEffect(() => {
    if (currentTestId === bid && testData && testData[bid]) {
      // if the data in DB is not {}
      if (Object.keys(testData[bid]).length) {
        testResults.current = testData[bid].tests;
        setElapse(testData[bid].timeSpent);
        setPoints(testData[bid].points);
        setTestNum(exercises.length);
      }
      setDataReady(true);
    }
  }, [currentTestId]);

  useEffect(() => {
    let timer: any;
    if (s_timer) {
      timer = setInterval(
        // We cannot use setElapse(++s_elapse), since when we setup this callback, s_elapse is 0. This event handler
        // belong to that render/state. The setElapse triggers rendering a new state. But in the state when this callback setup,
        // the s_elapse is still and always zero.
        () => setElapse(++elapse),
        1000,
      );
    }
    return () => clearInterval(timer);
  }, [s_timer]);

  const testActions = (params: TestActionParams) => {
    switch (params.action) {
      case "next":
        if (s_testNum >= 0) {
          testResults.current.push({
            _id: exercises[s_testNum]._id,
            ...params.data,
          });
          testPts.current.push(params.data.correct ? points[s_testNum] : 0);
        }
        if (s_testNum === exercises.length - 1) {
          // When 'Next' clicked and we are at the last test item, stop timer and get total points
          setTimer(false);
          setPoints(testPts.current.reduce((a, b) => a + b, 0));
        }
        setTestNum(s_testNum + 1);
        break;

      case "quit":
        // we don't save user's answer, but we still need 'rand' (if any) to allow user see the same question in report.
        testResults.current.push({
          _id: exercises[s_testNum]._id,
          ...params.data,
        });
        setOwnTest(user._id, bid, {
          timeSpent: s_elapse,
          done: false,
          tests: testResults.current,
          points: testPts.current.reduce((a, b) => a + b, 0),
        });
        onAction({ action: "ibx", str: null });
        break;
    }
  };

  const warningMsg = (time: number) => {
    const item = isTest ? "test" : "assignment";
    return [
      <p>{`After you start, you can only view the ${item} result by revisiting this ${item}.`}</p>,
      <p>
        {`If you quit before completing the ${item}, you cannot re-take the ${item}.`}
        {isTest && (
          <span>
            &nbsp;The estimated completion time is <b>{convertTime(time)}</b>.
          </span>
        )}
      </p>,
      <p>{`Please allocate enough time to take this ${item}. Once it has started, it cannot be paused.`}</p>,
    ];
  };

  const confirmChk = ({
    style,
    checked,
    label,
    action,
  }: {
    style: string;
    checked: boolean;
    label: string;
    action: () => void;
  }) => (
    <FormControlLabel
      classes={{ label: style }}
      control={<Checkbox color="primary" />}
      checked={checked}
      label={label}
      onChange={action}
    />
  );

  const renderInitPage = () => {
    return s_dataReady ? (
      <div className={classes.initWarn}>
        {warningMsg(timeLimit).map((msg, i) => (
          <div key={i}>{msg}</div>
        ))}
        {confirmChk({
          style: classes.chkbox,
          checked: confirm1,
          label: sTestConfirm,
          action: () => setConfirm1(!confirm1),
        })}
        {confirmChk({
          style: classes.chkbox2,
          checked: confirm2,
          label: `I understand I can only take this ${
            isTest ? "test" : "assignment"
          } once.`,
          action: () => setConfirm2(!confirm2),
        })}
        <Button
          style={{ float: "right", margin: "1rem 0" }}
          disabled={!confirm1 || !confirm2}
          variant="contained"
          color="primary"
          size="large"
          onClick={() => {
            setTestNum(0);
            setTimer(true);
          }}>
          Start Test
        </Button>
      </div>
    ) : (
      progressCircles()
    );
  };

  const renderTest = () => {
    if (s_testNum === exercises.length) {
      // render the report of each test
      const ret = exercises.map((ex: any, idx: number) => {
        const TagName = ExComponent[ex.type - 1];

        // find the index in testResults, though it should be idx, we keep the minor possibility the
        // ibx.exercises is changed after first published (either items or sequence)
        const tid = testResults.current.findIndex(
          (result: any) => result._id === ex._id,
        );

        return (
          <div key={idx} style={{ borderBottom: "2px solid #2D73C4" }}>
            <TagName
              exercise={{ ...ex, fbk }}
              quit={false}
              testResult={testResults.current[tid] || {}}
              onAction={(params: TestActionParams) => testActions(params)}
            />
          </div>
        );
      });

      return (
        <>
          {ret}
          {isTest && (
            <div className={classes.mainReport}>
              The score is <u>{s_points}</u>.&nbsp;
              {s_points === 100 ? `Great job! ` : ""}
            </div>
          )}
        </>
      );
    } else {
      const ex = exercises[s_testNum];
      const TagName = ExComponent[ex.type - 1];
      return (
        <TagName
          exercise={ex}
          quit={s_quit}
          onAction={(params: TestActionParams) => testActions(params)}
        />
      );
    }
  };

  const convertTime = (time: number) => {
    const min = Math.floor(time / 60);
    const sec = Math.floor(time % 60);
    const ret = min ? `${min} minute${min > 1 ? "s" : ""}` : "";
    return sec ? ret + ` ${sec} second${sec > 1 ? "s" : ""}` : ret;
  };

  const showTime = () => {
    const min = Math.floor(s_elapse / 60);
    const sec = Math.floor(s_elapse % 60);
    return (
      <p
        className={classNames({
          [classes.showTime]: true,
          [classes.blink_me]:
            timeLimit > 30 && timeLimit > s_elapse && timeLimit - s_elapse < 30,
          [classes.timeUp]: s_elapse >= timeLimit,
        })}>
        {min < 10 && "0"}
        {min} : {sec < 10 && "0"}
        {sec}
      </p>
    );
  };

  const exerciseActions = () => {
    return (
      <DialogActions
        classes={{ root: classes[`actions${isTest ? "Ex" : "Asm"}`] }}>
        <Button
          style={{ textTransform: "none" }}
          variant="outlined"
          color="inherit"
          size="large"
          onClick={handleClose}>
          {sBack}
        </Button>
        {isTest && showTime()}
      </DialogActions>
    );
  };

  const handleClose = () => {
    if (s_testNum >= 0 && s_testNum < exercises.length) {
      setAlertModal(
        <Modal
          title={sQuitTest}
          buttons={["Yes", "No"]}
          color="warning"
          onButtons={(id: number) => {
            if (id === 0) setQuit(true);
          }}
          onExit={() => setShowAlert(false)}>
          {sQuitTestMsg}
        </Modal>,
      );
      setShowAlert(true);
    } else {
      onAction({ action: "ibx", str: null });
    }
  };

  const showCompletedMsg = () => {
    const resetButton = (
      <Button
        style={{ float: "right" }}
        variant="contained"
        color="primary"
        size="large"
        onClick={() => {
          setOwnTest(user._id, bid, {});
          onAction({ action: "ibx", str: null });
        }}>
        Reset
      </Button>
    );

    let done = false;
    const success = s_elapse <= timeLimit;
    // if testData for this ibox is {} (not saved), and s_points has been assigned, then save it.
    if (Object.keys(testData[bid]).length === 0 && s_points !== -1) {
      setOwnTest(user._id, bid, {
        timeSpent: s_elapse,
        inTime: success,
        done: true,
        tests: testResults.current,
        points: s_points,
      });
      done = true;
    } else {
      done = testData[bid].done;
    }

    if (!isTest) {
      return (
        isCtMan(user.tc) !== false && (
          <div className={classes.mainReport}>{resetButton}</div>
        )
      );
    } else {
      const msg = done ? (
        <>
          {`The test was completed ${
            success
              ? " within the time limt."
              : ", but exceeded the time limit."
          }`}
          {` You spent ${convertTime(s_elapse)}. `}
          {success ? (
            <ThumbUp color="secondary" />
          ) : (
            <MoodBad color="primary" />
          )}
        </>
      ) : (
        <span>The test was incomplete.</span>
      );

      return (
        <div className={classes.mainReport}>
          <span>
            This test was taken on{" "}
            {moment(testData[bid].dateTaken).format("LLL")}
            .<br />
          </span>
          {msg}
          {isCtMan(user.tc) !== false && resetButton}
        </div>
      );
    }
  };

  const testTitle = () => {
    if (isTest) {
      return s_testNum >= 0
        ? s_testNum === exercises.length
          ? "Test Report"
          : "Test"
        : "Before You Start the Test";
    } else {
      return "Assignment";
    }
  };

  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("xs"));

  return (
    <>
      {showAlert && alertModal}
      <Dialog
        fullScreen={s_testNum >= 0 || fullScreen}
        open={true}
        onClose={handleClose}
        scroll={"paper"}>
        <DialogTitle
          id="scroll-dialog-title"
          classes={{ root: classes[`titleBar${isTest ? "Ex" : "Asm"}`] }}>
          <div className={classes.titleText}>
            <img
              src={require("assets/svg/apricot.svg")}
              className={classes.logo}
            />
            {testTitle()}
            <IconButton color="inherit" onClick={handleClose}>
              <Close />
            </IconButton>
          </div>
        </DialogTitle>
        <DialogContent style={{ minHeight: "12rem" }}>
          {s_testNum < 0 ? (
            renderInitPage()
          ) : (
            <>
              {s_testNum === exercises.length && showCompletedMsg()}
              {renderTest()}
            </>
          )}
        </DialogContent>
        {exerciseActions()}
      </Dialog>
    </>
  );
};

function mapStateToProps(state: any) {
  return {
    user: state.state_auth,
    bid: state.state_infobox.currentInfoBox._id,
    testData: state.state_own.testData,
    currentTestId: state.state_own.currentTestId,
  };
}

const Base: any = ExMainBase;
export const ExMain: any = withStyles(styles)(
  connect(mapStateToProps, { getOwnTest, setOwnTest })(Base),
);

// utility functions of each Test
//
export const reportCorrect = (classes: any) => (
  <div className={classes.reportCorrect}>Your answer is correct!</div>
);

export const reportWrong = (
  classes: any,
  skipped: boolean,
  msg: string | null = null,
) => (
  <>
    <div className={classes.reportWrong}>
      {skipped ? sSkipped : sAnsCorrect}
    </div>
    {msg && <div>{msg}</div>}
  </>
);

export const reportFeedback = (
  feedbackList: React.ReactNode,
  explain: string,
  fbk: number,
  classes: any,
) => {
  const showFeedback = (fbk & 0x01) !== 0;
  const showThinking = (fbk & 0x10) !== 0;
  return (
    <>
      {showFeedback && (
        <div className={classes.reportFeedback}>{sExFeedback}</div>
      )}
      {showFeedback && feedbackList !== null && feedbackList}
      {showThinking && explain !== "" && (
        <>
          <div className={classes.reportFeedback}>{sExThink}</div>
          <div>{showHtmlText(explain, classes)}</div>
        </>
      )}
    </>
  );
};

export const showHtmlText = (text: string, classes?: any) => {
  const HtmlToReactParser = require("html-to-react").Parser;
  const parser = new HtmlToReactParser();
  return (
    <span className={classes && classes.statement}>{parser.parse(text)}</span>
  );
};
